import { z } from 'zod';
import { TIPOS_PARTICIPANTE } from './campanha.js';

/**
 * Schemas do fluxo de cadastro pós-serviço (SPEC §6.1). São a fronteira
 * compartilhada entre a API (`POST /cadastro/*`, `POST /interno/cadastro/*`) e as
 * tools MCP do Hermes (SPEC §9). **Nenhuma regra de negócio aqui** — só formato.
 *
 * Telefone e documento chegam como texto cru: a normalização E.164 / dígitos
 * acontece **uma única vez, na API** (CLAUDE.md regra 4), com as funções deste
 * pacote. O schema não transforma para não normalizar duas vezes quando o Hermes
 * valida na fronteira da tool.
 */

export const veiculoCadastroSchema = z.object({
  placa: z.string().trim().min(1).max(10).toUpperCase(),
  marca: z.string().trim().min(1).optional(),
  modelo: z.string().trim().min(1).optional(),
  ano: z.coerce.number().int().gte(1900).lte(2100).optional(),
});
export type VeiculoCadastro = z.infer<typeof veiculoCadastroSchema>;

export const prepararCadastroSchema = z.object({
  nome: z.string().trim().min(1, 'Nome é obrigatório'),
  /** texto cru — normalizado para E.164 na API */
  telefone: z.string().trim().min(1, 'Telefone é obrigatório'),
  /** chave de idempotência (SPEC §6.1 / regra 5) */
  numeroOs: z.string().trim().min(1, 'Número da OS é obrigatório'),
  servico: z.string().trim().min(1, 'Descrição do serviço é obrigatória'),
  valor: z.coerce.number().nonnegative().optional(),
  /**
   * CPF/CNPJ do indicado (texto cru). Normalizado para dígitos + validado o DV
   * na API; documento inválido → `documento_invalido`. Alimenta a regra 5
   * (`mesmo_documento`) e a dedup por documento (D-OS-1).
   */
  documento: z.string().trim().min(1).optional(),
  email: z.string().trim().email().optional(),
  veiculo: veiculoCadastroSchema.optional(),
  /**
   * Atalho para `veiculo.placa` quando só a placa foi extraída da OS. Se
   * `veiculo` também vier, `veiculo.placa` prevalece (D-OS-1).
   */
  placa: z.string().trim().min(1).max(10).toUpperCase().optional(),
  /** ID do cliente no ERP "MECANICA VARGA" (`CLIENTE CODIGO:NN` na OS) — D-OS-6 */
  codigoErp: z.string().trim().min(1).max(32).optional(),
  /** data da OS conforme o PDF */
  finalizadaEm: z.coerce.date().optional(),
  /** código de indicação apresentado pelo indicado (texto cru) */
  codigoIndicacao: z.string().trim().min(1).optional(),
  /**
   * "É cliente novo?" — SEM default. SPEC §9 proíbe fallback silencioso: se o
   * operador não informou, o Hermes pergunta e não assume.
   */
  clienteNovo: z.boolean({ message: 'Informe se é cliente novo (sem default)' }),
  tipoParticipante: z.enum(TIPOS_PARTICIPANTE).default('cliente'),
});
export type PrepararCadastroInput = z.infer<typeof prepararCadastroSchema>;

export const confirmarCadastroSchema = z.object({
  rascunhoId: z.string().uuid(),
});
export type ConfirmarCadastroInput = z.infer<typeof confirmarCadastroSchema>;

/**
 * Confirmação via Hermes (`POST /interno/cadastro/confirmar`). A trava é **no
 * servidor** (CLAUDE.md regra 6): a API recusa um rascunho sem o carimbo de
 * confirmação humana — não é o prompt do agente que segura.
 */
export const confirmarCadastroInternoSchema = confirmarCadastroSchema.extend({
  confirmadoPorHumano: z.literal(true, {
    message: 'Confirmação humana explícita é obrigatória (CLAUDE.md regra 6)',
  }),
});
export type ConfirmarCadastroInternoInput = z.infer<typeof confirmarCadastroInternoSchema>;

export const buscarClienteInternoQuerySchema = z.object({
  telefone: z.string().trim().min(1),
});

// ── Respostas de `/v1/cadastro/*` (contrato do painel — UX-PAINEL-INTERNO §4) ──
// A API não declara response schema nessas rotas; estes schemas espelham o
// serviço (`apps/api/src/servicos/cadastro.ts`). Formato, nunca regra.

/** Dados interpretados/normalizados devolvidos por `POST /v1/cadastro/preparar`. */
export const dadosCadastroSchema = z.object({
  nome: z.string(),
  telefone: z.string(),
  numeroOs: z.string(),
  servico: z.string(),
  valor: z.number().optional(),
  documento: z.string().optional(),
  email: z.string().optional(),
  veiculo: veiculoCadastroSchema.optional(),
  placa: z.string().optional(),
  codigoErp: z.string().optional(),
  finalizadaEm: z.string().optional(),
  codigoIndicacao: z.string().optional(),
  clienteNovo: z.boolean(),
  tipoParticipante: z.enum(TIPOS_PARTICIPANTE),
});
export type DadosCadastro = z.infer<typeof dadosCadastroSchema>;

export const prepararCadastroRespostaSchema = z.object({
  rascunhoId: z.string(),
  dados: dadosCadastroSchema,
  avisos: z.array(z.string()),
});
export type PrepararCadastroResposta = z.infer<typeof prepararCadastroRespostaSchema>;

export const cadastroResumoIndicacaoSchema = z.object({
  id: z.string(),
  status: z.enum(['convertida', 'rejeitada']),
  motivo: z.string().optional(),
  nivelIndicador: z.number().int().optional(),
});

export const cadastroVoucherSchema = z.object({
  papel: z.string(),
  voucherCodigo: z.string(),
  descricao: z.string(),
});

/** Resposta de `POST /v1/cadastro/confirmar`. `idempotente: true` = replay calmo. */
export const confirmarCadastroRespostaSchema = z.object({
  idempotente: z.boolean(),
  cliente: z.object({
    id: z.string(),
    nome: z.string(),
    telefone: z.string(),
    origem: z.string(),
    jaEraClienteVarga: z.boolean(),
    novo: z.boolean(),
  }),
  codigo: z.string(),
  linkWa: z.string(),
  notificacaoId: z.string(),
  linkPrimeiroAcesso: z.string().nullable(),
  indicacao: cadastroResumoIndicacaoSchema.nullable(),
  vouchers: z.array(cadastroVoucherSchema),
});
export type ConfirmarCadastroResposta = z.infer<typeof confirmarCadastroRespostaSchema>;
